/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useSyncExternalStore } from 'react';

/** Tracks whether the current session was rejected with 403 to prevent a redirect loop. */
let forbidden = false;
/**
 * Tracks whether an API call returned 401. MSAL keeps a client-side token after the
 * backend session expires, so the route guard needs this flag to keep blocking protected
 * pages until the user re-authenticates. Cleared on a full reload (i.e. the sign-in redirect).
 */
let expired = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Record that the session is authenticated but not authorized for eDiagnostics. */
export function markForbidden(): void {
  if (forbidden) return;
  forbidden = true;
  emit();
}

/** Clear the forbidden state (e.g. before re-authenticating). */
export function clearForbidden(): void {
  if (!forbidden) return;
  forbidden = false;
  emit();
}

/** Current forbidden state (non-reactive). */
export function isForbidden(): boolean {
  return forbidden;
}

/** Record that the backend rejected the session with 401 so navigation stays gated. */
export function markExpired(): void {
  if (expired) return;
  expired = true;
  emit();
}

/** Clear the expired state (e.g. before re-authenticating). */
export function clearExpired(): void {
  if (!expired) return;
  expired = false;
  emit();
}

/** Current expired state (non-reactive). */
export function isExpired(): boolean {
  return expired;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React hook that re-renders when the forbidden state changes. */
export function useForbidden(): boolean {
  return useSyncExternalStore(subscribe, isForbidden, isForbidden);
}

/** React hook that re-renders when the expired state changes. */
export function useSessionExpired(): boolean {
  return useSyncExternalStore(subscribe, isExpired, isExpired);
}
